// CategoryPage.tsx — Co Marca Admin — estilo AboutusProjects
import Loading from '../../../components/Loading';
import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Trash2, Plus, History, Store, ChevronRight, Home } from 'lucide-react';
import { fetchCategories } from '../../../services/categoryService';
import CategoryModal from '../../../components/Category/CategoryModal';
import EditCategoryModal from '../../../components/Category/EditCategoryModal';
import Pagination from '../../../components/Pagination/Pagination';
import DeleteCategoryModal from '../../../components/Category/DeleteCategoryModal';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

interface SucursalPreview {
    id: number;
    nombre: string;
    codigo: string;
}

interface Category {
    id: number;
    nombre: string;
    descripcion?: string;
    imagen?: string;
    estado: boolean;
    fecha_creacion: string;
    sucursales_details?: SucursalPreview[];
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');

.cat-page { font-family: 'DM Sans', sans-serif; }

@keyframes sectionFadeUp {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(193, 52, 139,.35); }
    50%     { box-shadow:0 0 0 5px rgba(193, 52, 139,0); }
}
@keyframes blobFloat {
    0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
    50%     { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}

.cat-fadeup   { animation: sectionFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.cat-fadeup-1 { animation: sectionFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }

.cat-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:9px 20px; font-size:.83rem; font-weight:800;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(193, 52, 139,.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);
    white-space:nowrap;
}
.cat-btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 22px rgba(193, 52, 139,.32); }

.cat-btn-secondary {
    display:inline-flex; align-items:center; gap:7px;
    background:#fff; border:1.5px solid #e2e8f0;
    color:#374151; border-radius:11px;
    padding:9px 16px; font-size:.83rem; font-weight:700;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    transition:all .2s;
}
.cat-btn-secondary:hover { background:#f8fafc; border-color:${colors.secondary}40; color:${colors.secondary}; }

.cat-table-row { transition:background .15s; }
.cat-table-row:hover { background:#fafcff; }

.cat-action-btn {
    width:30px; height:30px; border-radius:9px; border:none;
    display:inline-flex; align-items:center; justify-content:center;
    cursor:pointer; transition:all .2s;
}

.custom-scroll::-webkit-scrollbar { height:6px; width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color:rgba(97, 37, 123,0.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color:rgba(97, 37, 123,0.5); }
`;

const CategoryPage: React.FC = () => {
    const [categories, setCategories]           = useState<Category[]>([]);
    const [isModalOpen, setIsModalOpen]         = useState(false);
    const [editModalOpen, setEditModalOpen]     = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage]         = useState(1);
    const itemsPerPage = 10;
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('category', userLevelId);

    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl]       = useState<string | null>(null);

    const loadCategories = useCallback(async () => {
        try {
            if (perm?.can_read) {
                const data = await fetchCategories();
                setCategories(data.sort((a: Category, b: Category) => a.id - b.id));
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error('Error al cargar categorías:', error);
            setCategories([]);
        }
    }, [perm]);

    useEffect(() => { loadCategories(); }, [loadCategories]);

    const totalPages        = Math.ceil(categories.length / itemsPerPage);
    const startIndex        = (currentPage - 1) * itemsPerPage;
    const displayedCategories = categories.slice(startIndex, startIndex + itemsPerPage);

    const handleImagePreview = (url: string) => { setPreviewImageUrl(url); setIsImagePreviewOpen(true); };
    const handleEditClick    = (cat: Category) => { setSelectedCategory(cat); setEditModalOpen(true); };
    const handleDeleteClick  = (cat: Category) => { setCategoryToDelete(cat); setDeleteModalOpen(true); };

    if (perm === null) return <Loading text="Cargando categorías..." />;

    return (
        <>
            <style>{css}</style>

            {/* Preview imagen */}
            {isImagePreviewOpen && previewImageUrl && (
                <div onClick={()=>setIsImagePreviewOpen(false)} style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,.75)', backdropFilter:'blur(4px)' }}>
                    <img src={previewImageUrl} alt="Preview" style={{ maxWidth:'90vw', maxHeight:'90vh', borderRadius:'16px', boxShadow:'0 32px 64px rgba(0,0,0,.4)' }}/>
                </div>
            )}

            <div className="cat-page" style={{ background:'#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* ── PAGE HEADER ── */}
                <div className="cat-fadeup" style={{ marginBottom:'28px' }}>
                    {/* Breadcrumb */}
                    <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'12px' }}>
                        <a href="/dashboard" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px', transition:'color .2s' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            <Home size={11}/> Dashboard
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <span style={{ color:colors.primary, fontWeight:'700' }}>Categorías</span>
                    </nav>

                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
                        <div>
                            <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1, marginBottom:'6px' }}>
                                Gestión de <span style={{ color:colors.primary }}>Categorías</span>
                            </h1>
                            <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500' }}>
                                Administra las categorías de servicios del sistema
                            </p>
                            <div style={{ display:'flex', gap:'5px', marginTop:'10px' }}>
                                {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>
                        </div>

                        {/* Botones */}
                        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
                            <button className="cat-btn-secondary" onClick={()=>navigate('/categories/history')} title="Historial">
                                <History size={15} color={colors.secondary}/>
                                <span style={{ color:colors.secondary }}>Historial</span>
                            </button>
                            {perm.can_create && (
                                <button className="cat-btn-primary" onClick={()=>setIsModalOpen(true)}>
                                    <Plus size={15}/> Agregar Categoría
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── TABLA ── */}
                <div className="cat-fadeup-1" style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', boxShadow:'0 4px 24px rgba(0,0,0,.04)', overflow:'hidden' }}>

                    {/* Modals */}
                    <CategoryModal    isOpen={isModalOpen}    onClose={()=>setIsModalOpen(false)}    onCreated={loadCategories}/>
                    <EditCategoryModal isOpen={editModalOpen}  onClose={()=>setEditModalOpen(false)}  category={selectedCategory} onUpdated={loadCategories}/>
                    <DeleteCategoryModal isOpen={deleteModalOpen} onClose={()=>setDeleteModalOpen(false)} category={categoryToDelete} onDeleted={loadCategories}/>
                    <ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/>

                    {/* Contador */}
                    <div style={{ padding:'16px 24px', borderBottom:'1.5px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:`${colors.primary}12`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <Store size={15} color={colors.primary}/>
                            </div>
                            <div>
                                <span style={{ fontSize:'.82rem', fontWeight:'800', color:'#0f172a' }}>{categories.length}</span>
                                <span style={{ fontSize:'.78rem', color:'#94a3b8', fontWeight:'500', marginLeft:'4px' }}>categorías registradas</span>
                            </div>
                        </div>
                        <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'600' }}>
                            Página {currentPage} de {totalPages || 1}
                        </span>
                    </div>

                    <div className="custom-scroll" style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:"'DM Sans',sans-serif" }}>
                            <thead>
                                <tr style={{ background:'#fafcff', borderBottom:'1.5px solid #f1f5f9' }}>
                                    {['Nombre','Descripción','Sucursales','Imagen','Fecha de creación','Estado','Acciones'].map((h,i)=>(
                                        <th key={i} style={{ padding:'12px 18px', textAlign:'left', fontSize:'.68rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayedCategories.map((cat, idx) => (
                                    <tr key={cat.id} className="cat-table-row" style={{ borderBottom:'1px solid #f8fafc' }}>
                                        {/* Nombre */}
                                        <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                            <span style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a' }}>{cat.nombre}</span>
                                        </td>

                                        {/* Descripción */}
                                        <td style={{ padding:'14px 18px', maxWidth:'220px' }}>
                                            <span style={{ fontSize:'.82rem', color:'#64748b', display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                                {cat.descripcion || <span style={{ color:'#cbd5e1', fontStyle:'italic' }}>Sin descripción</span>}
                                            </span>
                                        </td>

                                        {/* Sucursales */}
                                        <td style={{ padding:'14px 18px' }}>
                                            <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                                                {cat.sucursales_details && cat.sucursales_details.length > 0 ? (
                                                    cat.sucursales_details.map(suc=>(
                                                        <span key={suc.id} style={{ display:'inline-flex', alignItems:'center', background:`${colors.secondary}10`, border:`1px solid ${colors.secondary}22`, color:colors.secondary, borderRadius:'6px', padding:'2px 8px', fontSize:'.68rem', fontWeight:'700' }}>
                                                            {suc.codigo || suc.nombre}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span style={{ fontSize:'.72rem', color:'#cbd5e1', fontStyle:'italic' }}>Sin asignar</span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Imagen */}
                                        <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                            {cat.imagen ? (
                                                <img
                                                    src={cat.imagen}
                                                    alt="img"
                                                    onClick={()=>handleImagePreview(cat.imagen!)}
                                                    style={{ width:'44px', height:'44px', objectFit:'cover', borderRadius:'10px', border:'1.5px solid #f1f5f9', cursor:'pointer', transition:'transform .2s, box-shadow .2s' }}
                                                    onMouseEnter={e=>{ (e.currentTarget as HTMLImageElement).style.transform='scale(1.08)'; (e.currentTarget as HTMLImageElement).style.boxShadow=`0 8px 20px ${colors.primary}22`; }}
                                                    onMouseLeave={e=>{ (e.currentTarget as HTMLImageElement).style.transform='scale(1)'; (e.currentTarget as HTMLImageElement).style.boxShadow='none'; }}
                                                />
                                            ) : (
                                                <div style={{ width:'44px', height:'44px', borderRadius:'10px', background:'#f8fafc', border:'1.5px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    <Store size={16} color="#cbd5e1"/>
                                                </div>
                                            )}
                                        </td>

                                        {/* Fecha */}
                                        <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                            <span style={{ fontSize:'.78rem', color:'#64748b', fontWeight:'500' }}>
                                                {new Date(cat.fecha_creacion).toLocaleString('es-BO', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:false })}
                                            </span>
                                        </td>

                                        {/* Estado */}
                                        <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                            {cat.estado ? (
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.secondaryLight}14`, border:`1px solid ${colors.secondaryLight}30`, color:'#065f46', borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                    <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.secondaryLight, display:'inline-block' }}/>
                                                    Activo
                                                </span>
                                            ) : (
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.primary}10`, border:`1px solid ${colors.primary}22`, color:colors.primaryDark, borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                    <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.primary, display:'inline-block' }}/>
                                                    Inactivo
                                                </span>
                                            )}
                                        </td>

                                        {/* Acciones */}
                                        <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                            <div style={{ display:'flex', gap:'6px' }}>
                                                {perm.can_update && (
                                                    <button
                                                        className="cat-action-btn"
                                                        onClick={()=>handleEditClick(cat)}
                                                        title="Editar"
                                                        style={{ background:`${colors.secondary}12`, color:colors.secondary }}
                                                        onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.secondary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                        onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.secondary}12`; (e.currentTarget as HTMLButtonElement).style.color=colors.secondary; }}
                                                    >
                                                        <Edit size={14}/>
                                                    </button>
                                                )}
                                                {perm.can_delete && (
                                                    <button
                                                        className="cat-action-btn"
                                                        onClick={()=>handleDeleteClick(cat)}
                                                        title="Eliminar"
                                                        style={{ background:`${colors.primary}10`, color:colors.primary }}
                                                        onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.primary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                        onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.primary}10`; (e.currentTarget as HTMLButtonElement).style.color=colors.primary; }}
                                                    >
                                                        <Trash2 size={14}/>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                                {/* Empty state */}
                                {perm.can_read && categories.length === 0 && (
                                    <tr>
                                        <td colSpan={7} style={{ padding:'60px 24px', textAlign:'center' }}>
                                            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
                                                <div style={{ width:'56px', height:'56px', borderRadius:'16px', background:`${colors.primary}10`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    <Store size={24} color={colors.primary} style={{ opacity:.5 }}/>
                                                </div>
                                                <p style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>No hay categorías disponibles</p>
                                                {perm.can_create && (
                                                    <p style={{ fontSize:'.82rem', color:'#94a3b8' }}>Agrega una nueva categoría para comenzar</p>
                                                )}
                                                <div style={{ display:'flex', gap:'5px', marginTop:'4px' }}>
                                                    {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                                        <div key={i} style={{ height:'2px', width:i===0?'16px':'6px', borderRadius:'2px', background:c, opacity:.4 }}/>
                                                    ))}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    {perm.can_read && categories.length > 0 && (
                        <div style={{ borderTop:'1.5px solid #f1f5f9' }}>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                itemsPerPage={itemsPerPage}
                                totalItems={categories.length}
                            />
                        </div>
                    )}
                </div>

            </div>
        </>
    );
};

export default CategoryPage;